/**
 * Change Password Modal Component
 *
 * Modal for changing user password with validation
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { accountService } from '../../services/accountService';

type ChangePasswordFormData = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

interface ChangePasswordModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export function ChangePasswordModal({ onClose, onSuccess }: ChangePasswordModalProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const changePasswordSchema = z
    .object({
      currentPassword: z
        .string()
        .min(1, t('account.security.modal.current') + ' không được để trống'),
      newPassword: z
        .string()
        .min(8, 'Mật khẩu phải có ít nhất 8 ký tự')
        .regex(/[A-Z]/, 'Mật khẩu phải có ít nhất 1 chữ hoa')
        .regex(/[a-z]/, 'Mật khẩu phải có ít nhất 1 chữ thường')
        .regex(/[0-9]/, 'Mật khẩu phải có ít nhất 1 số'),
      confirmPassword: z.string(),
    })
    .refine(data => data.newPassword === data.confirmPassword, {
      message: t('account.security.modal.matchError'),
      path: ['confirmPassword'],
    });
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
  });

  const onSubmit = async (data: ChangePasswordFormData) => {
    try {
      setError(null);
      await accountService.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Có lỗi xảy ra khi đổi mật khẩu');
    }
  };

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm'>
      <div className='fixed inset-0' onClick={onClose} aria-hidden='true'></div>
      <div className='relative bg-white dark:bg-[#1E293B] rounded-xl shadow-xl p-6 w-full max-w-md mx-4 border border-gray-200 dark:border-gray-700'>
        <div className='flex items-center justify-between mb-6'>
          <h2 className='text-xl font-bold text-gray-900 dark:text-white'>
            {t('account.security.modal.title')}
          </h2>
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary rounded'
            aria-label='Close'
          >
            <span className='material-symbols-outlined'>close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
              {t('account.security.modal.current')}
            </label>
            <input
              {...register('currentPassword')}
              type='password'
              className='w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2 focus:border-primary focus:ring-primary focus:outline-none'
            />
            {errors.currentPassword && (
              <p className='text-xs text-red-600 dark:text-red-400 mt-1'>
                {errors.currentPassword.message}
              </p>
            )}
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
              {t('account.security.modal.new')}
            </label>
            <input
              {...register('newPassword')}
              type='password'
              className='w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2 focus:border-primary focus:ring-primary focus:outline-none'
            />
            {errors.newPassword && (
              <p className='text-xs text-red-600 dark:text-red-400 mt-1'>
                {errors.newPassword.message}
              </p>
            )}
            <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
              {t('account.security.modal.hint')}
            </p>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
              {t('account.security.modal.confirm')}
            </label>
            <input
              {...register('confirmPassword')}
              type='password'
              className='w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2 focus:border-primary focus:ring-primary focus:outline-none'
            />
            {errors.confirmPassword && (
              <p className='text-xs text-red-600 dark:text-red-400 mt-1'>
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {error && (
            <div className='rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-3'>
              <p className='text-sm text-red-700 dark:text-red-400'>{error}</p>
            </div>
          )}

          <div className='flex items-center justify-end gap-3 pt-4'>
            <button
              type='button'
              onClick={onClose}
              disabled={isSubmitting}
              className='px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 font-medium text-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50'
            >
              {t('account.security.modal.cancel')}
            </button>
            <button
              type='submit'
              disabled={isSubmitting}
              className='px-6 py-2 rounded-lg bg-primary text-text-main font-bold text-sm shadow-sm hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50'
            >
              {isSubmitting
                ? t('account.security.modal.processing')
                : t('account.security.modal.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
